<template>
  <div
    v-if="featureEnabled"
    class="ajcw"
    :class="rootClasses"
    role="dialog"
    aria-modal="true"
    :aria-labelledby="TITLE_ID"
    data-testid="add-job-category-wizard"
  >
    <header class="ajcw-header">
      <div class="ajcw-header__copy">
        <h2 :id="TITLE_ID" class="ajcw-title">{{ headerTitle }}</h2>
        <p v-if="showCategorySubtitle" class="ajcw-subtitle" data-testid="wizard-category-subtitle">
          Job Category: {{ name }}
        </p>
      </div>
      <div class="ajcw-header__meta">
        <span class="ajcw-step-label" data-testid="wizard-step-label">
          Step {{ stepNumber }} of {{ STEPS.length }}
        </span>
        <button
          type="button"
          class="ajcw-close"
          aria-label="Close Add Job Category wizard"
          data-testid="wizard-close"
          @click="emit('close')"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M18 6 6 18M6 6l12 12"
              stroke="#667085"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
        </button>
      </div>
    </header>

    <div class="ajcw-body">
      <!-- ── Step 1: Info — job category name ── -->
      <section v-if="step === STEP.INFO" class="ajcw-step" data-testid="step-info">
        <p class="ajcw-step__intro">
          Give the new job category a name. It will be used to group equal-value jobs across the
          organisation.
        </p>
        <div class="ajcw-field">
          <label class="ajcw-label" :for="NAME_INPUT_ID">
            Job category name
            <span class="ajcw-required" aria-hidden="true">*</span>
          </label>
          <input
            :id="NAME_INPUT_ID"
            class="ajcw-input"
            :class="{ 'ajcw-input--filled': hasName }"
            type="text"
            :value="name"
            placeholder="e.g. Management"
            autocomplete="off"
            required
            :aria-required="true"
            data-testid="wizard-name-input"
            @input="onNameInput"
          />
          <p v-if="!hasName" class="ajcw-field__hint" data-testid="wizard-name-hint">
            The name is required before you can continue.
          </p>
        </div>
      </section>

      <!-- ── Steps 2–5: Skills / Responsibility / Effort / Working Conditions ── -->
      <section v-else-if="activeFactor" class="ajcw-step" :data-testid="`step-${activeFactor.key}`">
        <p class="ajcw-step__intro">
          Score each of the {{ activeFactor.subfactors.length }} subfactors from 0 (does not apply) to
          its highest level.
        </p>
        <div class="ajcw-subfactors">
          <div
            v-for="subfactor in activeFactorSubviews"
            :key="subfactor.key"
            class="ajcw-subfactor"
            :class="{ 'ajcw-subfactor--scored': isScored(subfactor.score, subfactor.maxLevel) }"
            :data-testid="`subfactor-${subfactor.key}`"
          >
            <div class="ajcw-subfactor__head">
              <span :id="subfactorLabelId(subfactor.key)" class="ajcw-subfactor__name">
                {{ subfactor.label }}
              </span>
              <span
                class="ajcw-subfactor__badge"
                :class="{ 'ajcw-subfactor__badge--empty': !isScored(subfactor.score, subfactor.maxLevel) }"
                data-testid="subfactor-score"
              >
                {{ scoreBadge(subfactor.score, subfactor.maxLevel) }}
              </span>
            </div>

            <div class="ajcw-slider" :aria-labelledby="subfactorLabelId(subfactor.key)">
              <div class="ajcw-slider__rail" aria-hidden="true" />
              <div class="ajcw-slider__fill-zone" aria-hidden="true">
                <!--
                  Dynamic geometry only (like the JobCategoriesList gauge fill): the
                  fill width is derived from the selected 0–5 / 0–8 level.
                -->
                <span
                  class="ajcw-slider__fill"
                  :class="{ 'ajcw-slider__fill--active': isScored(subfactor.score, subfactor.maxLevel) }"
                  :style="{ width: fillPercent(subfactor.score, subfactor.maxLevel) + '%' }"
                />
              </div>
              <div class="ajcw-slider__stops">
                <EXTooltip
                  v-for="tip in subfactor.levelTooltips"
                  :key="tip.level"
                  placement="top"
                  :title="tip.title"
                  :description="tip.description"
                  :delay="0"
                  popper-class="ajcw-level-tooltip"
                  show-on-child-focus
                >
                  <button
                    type="button"
                    class="ajcw-slider__stop"
                    :class="{ 'ajcw-slider__stop--active': subfactor.score === tip.level }"
                    :aria-pressed="String(subfactor.score === tip.level)"
                    :aria-label="`${subfactor.label}: level ${tip.level} of ${subfactor.maxLevel}`"
                    :data-testid="`slider-${subfactor.key}-${tip.level}`"
                    @click="onScore(activeFactor.key, subfactor.key, tip.level)"
                  />
                </EXTooltip>
              </div>
              <div class="ajcw-slider__scale" aria-hidden="true">
                <span
                  v-for="level in subfactor.scoreLevels"
                  :key="level"
                  class="ajcw-slider__scale-label"
                  :class="{ 'ajcw-slider__scale-label--zero': level === 0 }"
                >
                  {{ scaleLabel(level) }}
                </span>
              </div>
            </div>

            <div class="ajcw-subfactor__meta">
              <p class="ajcw-subfactor__description">{{ subfactor.description }}</p>
              <p
                class="ajcw-subfactor__level"
                :class="{ 'ajcw-subfactor__level--empty': !isScored(subfactor.score, subfactor.maxLevel) }"
                data-testid="subfactor-level"
              >
                {{ levelText(subfactor.score, subfactor.maxLevel) }}
              </p>
              <p
                v-if="subfactor.levelDescription"
                class="ajcw-subfactor__level-description"
                data-testid="subfactor-level-description"
              >
                {{ subfactor.levelDescription }}
              </p>
            </div>
          </div>
        </div>
      </section>

      <!-- ── Step 6: Review ── -->
      <section v-else-if="step === STEP.REVIEW" class="ajcw-step" data-testid="step-review">
        <p class="ajcw-step__intro">Review the 4-factor scores before creating the job category.</p>
        <div class="ajcw-review" data-testid="review-totals">
          <div
            v-for="row in reviewRows"
            :key="row.key"
            class="ajcw-review__row"
            :data-testid="`review-row-${row.key}`"
          >
            <span class="ajcw-review__label">{{ row.label }}</span>
            <span
              class="ajcw-review__total"
              :class="{ 'ajcw-review__total--empty': !row.complete }"
              data-testid="review-row-total"
            >
              {{ row.display }}
            </span>
          </div>
        </div>
      </section>

      <p v-if="apiError" class="ajcw-error" role="alert" data-testid="wizard-api-error">
        {{ apiError }}
      </p>
    </div>

    <footer class="ajcw-actions">
      <button
        v-if="step === STEP.INFO"
        type="button"
        class="ajcw-btn ajcw-btn--secondary"
        data-testid="wizard-cancel"
        @click="emit('close')"
      >
        Cancel
      </button>
      <button
        v-else
        type="button"
        class="ajcw-btn ajcw-btn--secondary"
        data-testid="wizard-back"
        @click="emit('back')"
      >
        Back
      </button>

      <button
        v-if="!isReviewStep"
        type="button"
        class="ajcw-btn ajcw-btn--primary"
        :class="{ 'ajcw-btn--disabled': !canProceed }"
        :disabled="!canProceed"
        data-testid="wizard-next"
        @click="onNext"
      >
        {{ NEXT_BUTTON_LABEL }}
      </button>
      <button
        v-else
        type="button"
        class="ajcw-btn ajcw-btn--primary"
        :class="{ 'ajcw-btn--disabled': !canSubmit }"
        :disabled="!canSubmit"
        data-testid="wizard-submit"
        @click="onSubmit"
      >
        {{ CREATE_BUTTON_LABEL }}
      </button>
    </footer>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref } from "vue";

import EXTooltip from "@components/tooltip/EXTooltip.vue";
import { LD_FLAG_IDEA_MDP_566 } from "@components/constants/launchDarklyFlagKeys";
import {
  CREATE_BUTTON_LABEL,
  FACTORS,
  NEXT_BUTTON_LABEL,
  STEP,
  STEPS,
  WIZARD_TITLE,
  buildSubmitPayload,
  factorForStep,
  isScoreSet,
  normalizeScores,
  reviewRowView,
  stepFieldValues,
  stepIndex,
  stepIsComplete,
  subfactorLevelDescription,
  subfactorLevelTooltips,
  subfactorMaxLevel,
  subfactorScoreLevels,
  wizardIsComplete,
} from "./addJobCategoryWizard.js";

/**
 * ER-11356 — AddJobCategoryWizard (Organization > Job Categories).
 *
 * Six-step modal wizard for creating a new Job Category with the EIGE / European
 * Commission Gender-Neutral Job Evaluation Toolkit "4-Factor" method
 * (Info → Skills → Responsibility → Effort → Working Conditions → Review).
 *
 * Presentational component in the style of FirstPayPlanWizard: the parent owns the
 * current `step`, the draft `name` and the 0–5 / 0–8 `scores` (Knowledge tops
 * out at 8; every other subfactor at 5; 0 means "does not apply"), and reacts
 * to `next` /
 * `submit` by persisting. The wizard renders the step given by the `step` prop,
 * keeps every primary action disabled until that step's required inputs are filled,
 * and emits `close` / `back` / `next` / `submit`. No API calls inside the component.
 *
 * Shipped behind the linked idea flag (idea-MDP-566, "Job Category — Equal-Value
 * Grouping for Fair Pay (EU Directive Alignment)"). Kill-switch semantics: renders
 * by default; set the flag to false to keep the wizard closed.
 */
const props = defineProps({
  /** Active step key: "Info" | "Skills" | "Responsibility" | "Effort" | "Working Conditions" | "Review". */
  step: {
    type: String,
    default: STEP.INFO,
  },
  /** Presentation hint for the design's Empty/Filled states ("Empty" | "Filled"). */
  state: {
    type: String,
    default: "Empty",
    validator: (value) => value === "Empty" || value === "Filled",
  },
  /** Job category name (v-model). */
  name: {
    type: String,
    default: "",
  },
  /** Nested 0–5 / 0–8 subfactor scores (v-model), normalised to every subfactor. */
  scores: {
    type: Object,
    default: () => ({}),
  },
  /** Inline message for a rejected submit (e.g. a backend 400 on the scores); "" hides it. */
  apiError: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["update:name", "update:scores", "close", "back", "next", "submit"]);

const TITLE_ID = "ajcw-title";
const NAME_INPUT_ID = "ajcw-name-input";

const app = getCurrentInstance();
const { proxy } = app || {};
const featureEnabled = ref(true);

onMounted(async () => {
  try {
    featureEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_566, true)) !== false;
  } catch {
    featureEnabled.value = true;
  }
});

const normalizedScores = computed(() => normalizeScores(props.scores));

const hasName = computed(() => String(props.name || "").trim().length > 0);

const stepNumber = computed(() => stepIndex(props.step));

const isReviewStep = computed(() => props.step === STEP.REVIEW);

const activeFactor = computed(() => factorForStep(props.step));

const activeFactorSubviews = computed(() => {
  const factor = activeFactor.value;
  if (!factor) return [];
  return factor.subfactors.map((subfactor) => {
    const score = normalizedScores.value[factor.key][subfactor.key];
    const maxLevel = subfactorMaxLevel(subfactor);
    return {
      key: subfactor.key,
      label: subfactor.label,
      description: subfactor.description,
      score,
      maxLevel,
      scoreLevels: subfactorScoreLevels(subfactor),
      levelDescription: subfactorLevelDescription(subfactor, score),
      levelTooltips: subfactorLevelTooltips(subfactor),
    };
  });
});

const reviewRows = computed(() =>
  FACTORS.map((factor) => reviewRowView(normalizedScores.value, factor))
);

const canProceed = computed(() => stepIsComplete(props.step, props.name, normalizedScores.value));

const canSubmit = computed(() => wizardIsComplete(props.name, normalizedScores.value));

const headerTitle = computed(() => {
  if (props.step === STEP.INFO) return WIZARD_TITLE;
  if (props.step === STEP.REVIEW) return STEP.REVIEW;
  return activeFactor.value ? activeFactor.value.label : WIZARD_TITLE;
});

const showCategorySubtitle = computed(() => hasName.value && props.step !== STEP.INFO);

const rootClasses = computed(() => ({
  "ajcw--empty": props.state !== "Filled",
  "ajcw--filled": props.state === "Filled",
}));

function subfactorLabelId(subfactorKey) {
  return `ajcw-sub-${subfactorKey}`;
}

function isScored(value, maxLevel) {
  return isScoreSet(value, maxLevel);
}

function scoreBadge(value, maxLevel) {
  return isScoreSet(value, maxLevel) ? `${Number(value)} / ${maxLevel}` : "Not scored";
}

function fillPercent(value, maxLevel) {
  if (!isScoreSet(value, maxLevel)) return 0;
  const max = Number(maxLevel);
  return max > 0 ? (Number(value) / max) * 100 : 0;
}

function levelText(value, maxLevel) {
  return isScoreSet(value, maxLevel)
    ? `Level ${Number(value)} of ${maxLevel}`
    : `Select a level between 0 and ${maxLevel}`;
}

function scaleLabel(level) {
  return level === 0 ? "0 — Does not apply" : String(level);
}

function onNameInput(event) {
  emit("update:name", event.target.value);
}

function onScore(factorKey, subfactorKey, level) {
  const scores = normalizedScores.value;
  emit("update:scores", {
    ...scores,
    [factorKey]: { ...scores[factorKey], [subfactorKey]: level },
  });
}

function onNext() {
  if (!canProceed.value) return;
  emit("next", stepFieldValues(props.step, props.name, normalizedScores.value));
}

function onSubmit() {
  if (!canSubmit.value) return;
  emit("submit", buildSubmitPayload(props.name, normalizedScores.value));
}
</script>

<style scoped>
.ajcw {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 80vw;
  min-width: min(640px, 100%);
  max-width: 100%;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 20px 24px -4px rgba(16, 24, 40, 0.08), 0 8px 8px -4px rgba(16, 24, 40, 0.03);
  font-family: Inter, sans-serif;
  color: #101828;
}

.ajcw-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 24px 24px 16px;
}

.ajcw-header__copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.ajcw-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  color: #101828;
}

.ajcw-subtitle {
  margin: 0;
  font-size: 13px;
  line-height: 18px;
  color: #667085;
}

.ajcw-header__meta {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.ajcw-step-label {
  font-size: 13px;
  line-height: 18px;
  color: #667085;
  white-space: nowrap;
}

.ajcw-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  color: #667085;
}

.ajcw-close:hover {
  background: #f2f4f7;
}

.ajcw-close:focus-visible,
.ajcw-btn:focus-visible,
.ajcw-input:focus-visible,
.ajcw-slider__stop:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 1px;
}

.ajcw-body {
  padding: 8px 24px 0;
}

.ajcw-step {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.ajcw-step__intro {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: #667085;
}

.ajcw-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ajcw-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
}

.ajcw-required {
  color: #f04438;
}

.ajcw-input {
  box-sizing: border-box;
  width: 100%;
  height: 44px;
  padding: 10px 14px;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  font-family: inherit;
  font-size: 16px;
  line-height: 24px;
  color: #101828;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.ajcw-input::placeholder {
  color: #667085;
}

.ajcw-input--filled {
  border-color: #7f56d9;
}

.ajcw-field__hint {
  margin: 0;
  font-size: 13px;
  line-height: 18px;
  color: #667085;
}

.ajcw-error {
  margin: 16px 0 0;
  font-size: 14px;
  line-height: 20px;
  color: #b42318;
}

.ajcw-subfactors {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-bottom: 4px;
}

.ajcw-subfactor {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ajcw-subfactor__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}

.ajcw-subfactor__name {
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #344054;
}

.ajcw-subfactor__badge {
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 16px;
  background: #f9f5ff;
  color: #7f56d9;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
}

.ajcw-subfactor__badge--empty {
  background: #f2f4f7;
  color: #667085;
}

.ajcw-slider {
  position: relative;
}

.ajcw-slider__rail {
  position: absolute;
  top: 11px;
  right: 10px;
  left: 10px;
  height: 2px;
  background: #eaecf0;
  border-radius: 1px;
}

.ajcw-slider__fill-zone {
  position: absolute;
  top: 11px;
  right: 10px;
  left: 10px;
  height: 2px;
  pointer-events: none;
}

.ajcw-slider__fill {
  display: block;
  height: 2px;
  background: transparent;
}

.ajcw-slider__fill--active {
  background: #7f56d9;
}

.ajcw-slider__stops {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 24px;
}

.ajcw-slider__stop {
  display: block;
  width: 20px;
  height: 20px;
  padding: 0;
  background: #ffffff;
  border: 1.5px solid #d0d5dd;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.ajcw-slider__stop:hover {
  border-color: #7f56d9;
}

.ajcw-slider__stop--active {
  background: #7f56d9;
  border-color: #7f56d9;
}

.ajcw-slider__scale {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 4px;
  margin-top: 2px;
  padding: 0 4px;
}

.ajcw-slider__scale-label {
  flex: 0 1 auto;
  min-width: 12px;
  font-size: 12px;
  line-height: 16px;
  color: #667085;
  text-align: center;
}

.ajcw-slider__scale-label--zero {
  max-width: 88px;
  text-align: left;
}

.ajcw-subfactor__meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ajcw-subfactor__description {
  margin: 0;
  font-size: 13px;
  line-height: 18px;
  color: #667085;
}

.ajcw-subfactor__level {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  line-height: 18px;
  color: #7f56d9;
}

.ajcw-subfactor__level--empty {
  color: #667085;
  font-weight: 400;
}

.ajcw-subfactor__level-description {
  margin: 0;
  font-size: 13px;
  line-height: 18px;
  color: #344054;
}

.ajcw-review {
  display: flex;
  flex-direction: column;
}

.ajcw-review__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 0;
  border-top: 1px solid #e5e7ec;
}

.ajcw-review__row:first-child {
  border-top: none;
}

.ajcw-review__label {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
}

.ajcw-review__total {
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #344054;
}

.ajcw-review__total--empty {
  color: #667085;
  font-weight: 400;
}

.ajcw-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px;
}

.ajcw-btn {
  padding: 10px 16px;
  border-radius: 8px;
  font-family: inherit;
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.ajcw-btn--secondary {
  background: #ffffff;
  border: 1px solid #d0d5dd;
  color: #344054;
}

.ajcw-btn--primary {
  background: #7f56d9;
  border: 1px solid #7f56d9;
  color: #ffffff;
}

.ajcw-btn--disabled,
.ajcw-btn:disabled {
  background: #e0e0e0;
  border-color: #e0e0e0;
  color: #ffffff;
  cursor: not-allowed;
}
</style>

<style>
/* Popper is appended to <body> with an inline z-index (2001); the wizard overlay is 4000. */
.ajcw-level-tooltip {
  z-index: 4100 !important;
}
</style>
